import React, { useState } from "react"
import * as XLSX from "xlsx"
import { TACreateApplication } from "../services/applicationAPI"
import { TAfindUser } from "../services/userAPI"
import { TAuploadExcelFile, TAupdateExcelUploadStatus } from "../services/campaignsAPI"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { selectUser } from "../redux/store/auth/authSlice"
import { getErrorStatus, getErrorMessage } from "../types/error"

interface RowData {
  [key: string]: string | number | boolean | undefined
}

const requiredFields: string[] = [
  "yaş",
  "cinsiyet",
  "onay durumu",
  "kullanıcı tutarı",
  "marka tutarı",
]

const AddApplicationFromExcelModal = ({
  isOpen,
  onClose,
  _id,
  platform,
  onUploadComplete,
}: {
  isOpen: boolean
  onClose: () => void
  _id: string
  platform: string
  onUploadComplete?: () => void
}) => {
  const navigate = useNavigate()
  const userEmail = useSelector(selectUser)?.replace(/"/g, "") || ""
  const [rows, setRows] = useState<RowData[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [processing, setProcessing] = useState<boolean>(false)
  const [results, setResults] = useState<("success" | "failure")[]>([])
  const [uploadMode, setUploadMode] = useState<"process" | "upload">("process")
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState<boolean>(false)
  const [selectedFileName, setSelectedFileName] = useState<string>("")

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      return
    }

    if (uploadMode === "upload") {
      setSelectedFile(file)
      return
    }

    setSelectedFileName(file.name)

    const reader = new FileReader()
    reader.onload = (evt) => {
      const data = evt.target?.result
      const workbook = XLSX.read(data, { type: "binary" })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const json: RowData[] = XLSX.utils.sheet_to_json(sheet, { defval: null })
      const filtered = json.filter((row) =>
        Object.values(row).some((val) => val !== null && val !== undefined && val !== ""),
      )
      setRows(filtered)
      setErrors([])
      setResults([])
    }
    reader.readAsBinaryString(file)
  }

  const handleUploadFile = async () => {
    if (!selectedFile) {
      setErrors(["Lütfen bir dosya seçin"])
      return
    }

    setUploading(true)
    setErrors([])

    try {
      await TAuploadExcelFile(_id, selectedFile, userEmail)
      setSelectedFile(null)
      if (onUploadComplete) {
        onUploadComplete()
      }
      onClose()
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setErrors([`Dosya yükleme hatası: ${getErrorMessage(error) || error}`])
    } finally {
      setUploading(false)
    }
  }

  const validateRows = (): boolean => {
    const errs: string[] = []
    rows.forEach((row, idx) => {
      requiredFields.forEach((field) => {
        const val = row[field]
        if (
          val === null ||
          val === undefined ||
          val === "" ||
          (Array.isArray(val) && val.length === 0)
        ) {
          errs.push(`Satır ${idx + 1}: '${field}' alanı doldurulmalı`)
        }
      })
      if (platform === "instagram") {
        const insta = row["instagram kullanıcı adı"]
        if (!insta || String(insta).trim() === "") {
          errs.push(`Satır ${idx + 1}: 'instagram kullanıcı adı' alanı doldurulmalı`)
        }
      } else if (platform === "tiktok") {
        const tiktok = row["tiktok kullanıcı adı"]
        if (!tiktok || String(tiktok).trim() === "") {
          errs.push(`Satır ${idx + 1}: 'tiktok kullanıcı adı' alanı doldurulmalı`)
        }
      } else if (platform === "ugc") {
        const insta = row["instagram kullanıcı adı"]
        const tiktok = row["tiktok kullanıcı adı"]
        if ((!insta || String(insta).trim() === "") && (!tiktok || String(tiktok).trim() === "")) {
          errs.push(
            `Satır ${
              idx + 1
            }: 'instagram kullanıcı adı' veya 'tiktok kullanıcı adı' alanı doldurulmalı`,
          )
        }
      }
    })
    setErrors(errs)
    return errs.length === 0
  }

  const handleProcess = async () => {
    setErrors([])
    if (rows.length === 0) {
      setErrors(["Excel dosyasında veri bulunamadı"])
      return
    }
    if (!validateRows()) {
      return
    }

    setProcessing(true)
    const errs: string[] = []
    const res: ("success" | "failure")[] = []

    let uploadId: string | null = null
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet(rows)
    XLSX.utils.book_append_sheet(workbook, worksheet, "Applications")

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })

    const file = new File([blob], "applications.xlsx", {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })

    const uploadResponse = await TAuploadExcelFile(_id, file, userEmail)
    uploadId = uploadResponse.uploadId || uploadResponse._id

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      const instaUsername = String(row["instagram kullanıcı adı"] || "")
      const tiktokUsername = String(row["tiktok kullanıcı adı"] || "")
      const age = Number(row["yaş"])
      const gender = String(row["cinsiyet"] || "")
      const verification = row["onay durumu"] === true || row["onay durumu"] === "true"
      const priceUser = Number(row["kullanıcı tutarı"])
      const priceBrand = Number(row["marka tutarı"])
      const schoolType = row["okul türü(opsiyonel)"] || undefined
      const schoolName = row["okul adı(opsiyonel)"] || undefined
      const city = row["şehir(opsiyonel)"] || undefined
      const country = row["ülke(opsiyonel)"] || undefined
      const language = row["dil(opsiyonel)"] || undefined
      const currency = row["para birimi(opsiyonel)"] || undefined
      const job = row["meslek(opsiyonel)"] || undefined
      const birthdayRaw = row["doğum tarihi(opsiyonel)"]
      const birthday = birthdayRaw ? new Date(birthdayRaw as string) : undefined

      try {
        let userData: any = {}
        if ((platform === "instagram" || platform === "ugc") && instaUsername) {
          try {
            userData = await TAfindUser(`insta.username=${instaUsername}`)
            delete userData.tiktok
          } catch {
            userData = {}
          }
        } else if ((platform === "tiktok" || platform === "ugc") && tiktokUsername) {
          try {
            userData = await TAfindUser(`tiktok.username=${tiktokUsername}`)
            delete userData.insta
          } catch {
            userData = {}
          }
        }

        const payload = {
          user_id: userData?._id,
          insta_username: instaUsername,
          tiktok_username: tiktokUsername,
          age,
          gender,
          verification,
          school_type: schoolType,
          school_name: schoolName,
          city,
          country,
          language,
          currency,
          job,
          birthday,
          price_brand: priceBrand,
          price_user: priceUser,
          insta: userData?.insta,
          tiktok: userData?.tiktok,
          state: "first_application",
        }
        await TACreateApplication(payload, _id, "first_application", platform)
        res.push("success")
      } catch (err: unknown) {
        if (getErrorStatus(err) === 401) {
          navigate("/login")
        }
        res.push("failure")
        errs.push(`Satır ${i + 1}: ${getErrorMessage(err) || String(err)}`)
      }
    }

    if (uploadId) {
      const successCount = res.filter((r) => r === "success").length
      const failureCount = res.filter((r) => r === "failure").length

      await TAupdateExcelUploadStatus(_id, uploadId, "completed", successCount, failureCount, errs)
    }

    setProcessing(false)
    setErrors(errs)
    setResults(res)

    if (onUploadComplete) {
      onUploadComplete()
    }
  }

  const downloadResults = () => {
    const headers = [...Object.keys(rows[0] || {}), "durum"]
    const data = rows.map((row, idx) => [
      ...headers.slice(0, -1).map((h) => row[h]),
      results[idx] || "",
    ])
    const aoa = [headers, ...data]
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Sonuclar")
    XLSX.writeFile(wb, "application_results.xlsx")
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded shadow max-w-lg w-full max-h-[80vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">Excel&apos;den Başvuru Yükle</h3>

        <input type="file" accept=".xlsx,.xls" onChange={handleFile} className="mb-4" />

        {uploadMode === "process" && selectedFileName && (
          <div className="mb-4 p-3 bg-green-50 rounded">
            <p className="text-sm text-green-800">Seçilen dosya: {selectedFileName}</p>
          </div>
        )}

        {uploadMode === "upload" && selectedFile && (
          <div className="mb-4 p-3 bg-blue-50 rounded">
            <p className="text-sm text-blue-800">Seçilen dosya: {selectedFile.name}</p>
            <button
              onClick={handleUploadFile}
              disabled={uploading}
              className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {uploading ? "Yükleniyor..." : "Dosyayı Yükle"}
            </button>
          </div>
        )}

        {uploadMode === "process" && rows.length > 0 && (
          <div className="mt-4">
            <p>{rows.length} satır hazır.</p>
            <button
              onClick={handleProcess}
              disabled={processing}
              className="mt-2 px-4 py-2 bg-blue-500 text-white rounded"
            >
              {processing ? "İşleniyor..." : "Yüklemeye Başla"}
            </button>
            {results.length === rows.length && (
              <button
                onClick={downloadResults}
                className="mt-2 px-4 py-2 bg-green-500 text-white rounded ml-2"
              >
                Sonuçları İndir
              </button>
            )}
          </div>
        )}

        {errors.length > 0 && (
          <div className="mt-4 text-red-600">
            {errors.map((err, idx) => (
              <div key={idx}>{err}</div>
            ))}
          </div>
        )}

        <div className="mt-6 text-right">
          <button
            onClick={() => {
              onClose()
              setProcessing(false)
              setErrors([])
              setResults([])
              setRows([])
              setSelectedFile(null)
              setSelectedFileName("")
              setUploadMode("process")
            }}
            className="px-4 py-2 bg-gray-300 rounded"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  )
}

export default AddApplicationFromExcelModal
